import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2DataGrid,
  V2Badge,
  V2Button,
  V2EmptyState,
  Select,
  LoadingState,
  StatePanel,
  ConfirmDialog,
  Alert,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiDelete } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { findEntityConfig } from '../../lib/masterDataConfig.js';
import type { MasterDataListResponse, MasterDataRecord } from '../../lib/types/masterData.js';

type SortDirection = 'asc' | 'desc';

interface Row extends MasterDataRecord {
  readonly id: string;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0 §5.3 اطلاعات پایه — list pattern (shared across entities)
 * canonical pattern: V2DataGrid (v2 stock table), search + status filter + sort — the
 *   same real filter/sort convention every other real V2 list page in this
 *   app already uses (SuppliersPage, AccountsPage, ProjectListPage), on the
 *   V2 (`sk-*`) system this master-data trio was still missing (it stayed
 *   on v1 `DataTable`/`Input` while the rest of the app moved to V2).
 * Sort is a client-side "مرتب‌سازی" select (code/name, asc/desc) rather
 * than clickable column headers — `V2TableColumn` has no header-click/sort
 * affordance today, and adding one is a shared design-system change wider
 * than this page's own scope; the select gives the same real capability
 * without touching packages/design-system.
 *
 * Row "آرشیو" action (added against Sakhtar Pilot P0's "ARCHIVE RECORD ·
 * WARN" confirm-dialog kit): real `DELETE .../master-data/:entity/:id`
 * exists on all 6 controllers today (party.controller.ts:282,
 * product.controller.ts:242, uom.controller.ts:234, tax.controller.ts:255,
 * warehouse.controller.ts:249, warehouse-location.controller.ts:263 — all
 * a soft "set inactive", never a hard delete) but had no UI entry point
 * anywhere in this app. Uses `ConfirmDialog` with `danger={false}` (a
 * warn, not a destructive-delete, tone — the record survives and can be
 * reactivated from the edit form's status field) rather than the
 * `typeToConfirmValue` pattern, which this audit's own notes reserve for
 * genuinely destructive, hard-to-reverse actions.
 * state reference: ready · empty · permission_denied · network_error
 */
export function EntityListPage() {
  const { slug, entity } = useParams<{ slug: string; entity: string }>();
  const config = findEntityConfig(entity);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sortKey, setSortKey] = useState('code');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [pendingArchive, setPendingArchive] = useState<Row | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const base = slug ? tenantBasePath(slug) : '';

  const hasStatusField = config?.formFields.some((f) => f.key === 'status') ?? false;

  const query = new URLSearchParams();
  if (search.trim()) query.set('search', search.trim());
  if (hasStatusField && status) query.set('status', status);
  const qs = query.toString();

  const result = useApiGet<MasterDataListResponse>(
    slug && config ? `${base}/api/v1/master-data/${config.pathSegment}${qs ? `?${qs}` : ''}` : null,
    [slug, config?.pathSegment, qs],
  );

  const rows = useMemo<readonly Row[]>(() => {
    if (result.status !== 'ready' || !config) return [];
    const withId: Row[] = result.data.items.map((row) => ({
      ...row,
      id: String(row[config.idField]),
    }));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...withId].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [result, config, sortKey, sortDir]);

  if (!slug || !config) return null;

  async function onConfirmArchive() {
    if (!pendingArchive || !config) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await apiDelete(`${base}/api/v1/master-data/${config.pathSegment}/${pendingArchive.id}`);
      setPendingArchive(null);
      result.reload();
    } catch {
      setArchiveError(`آرشیو ${config.label} ناموفق بود.`);
    } finally {
      setArchiving(false);
    }
  }

  const columns: readonly V2TableColumn<Row>[] = [
    ...config.listColumns.map((col) => {
      const options = config.formFields.find((f) => f.key === col.key)?.options;
      const labelOf = (value: unknown): string =>
        options?.find((o) => o.value === value)?.label ?? String(value);
      return {
        key: col.key,
        header: col.label,
        render: (row: Row) => {
          const cell = row[col.key];
          return (
            <Link to={`${base}/master-data/${config.pathSegment}/${row[config.idField]}`}>
              {Array.isArray(cell)
                ? cell.length > 0
                  ? cell.map(labelOf).join('، ')
                  : '—'
                : col.date
                  ? displayJalaliDate(cell === null || cell === undefined ? null : String(cell))
                  : String(cell ?? '—')}
            </Link>
          );
        },
      };
    }),
    ...(hasStatusField
      ? [
          {
            key: 'status',
            header: 'وضعیت',
            render: (row: Row) =>
              row.status === 'active' ? (
                <V2Badge tone="success">فعال</V2Badge>
              ) : (
                <V2Badge tone="neutral">غیرفعال</V2Badge>
              ),
          },
          {
            key: 'actions',
            header: '',
            render: (row: Row) =>
              row.status === 'active' ? (
                <V2Button variant="ghost" size="sm" onClick={() => setPendingArchive(row)}>
                  آرشیو
                </V2Button>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={result.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon={config.icon}
          title={config.labelPlural}
          subtitle={`${rows.length.toLocaleString('fa-IR')} ${config.label}`}
          actions={
            <Link to={`${base}/master-data/${config.pathSegment}/new`}>
              <V2Button variant="primary" size="sm" icon="plus">
                {`ایجاد ${config.label}`}
              </V2Button>
            </Link>
          }
          search={{ value: search, onChange: setSearch, placeholder: `جستجوی ${config.label}` }}
          filters={
            hasStatusField
              ? [
                  {
                    id: 'status',
                    label: 'وضعیت',
                    value: status,
                    onChange: setStatus,
                    options: [
                      { value: '', label: 'همه' },
                      { value: 'active', label: 'فعال' },
                      { value: 'inactive', label: 'غیرفعال' },
                    ],
                  },
                ]
              : []
          }
          toolbarExtra={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Select
                aria-label="مرتب‌سازی"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
              >
                {config.listColumns.map((col) => (
                  <option key={col.key} value={col.key}>
                    مرتب‌سازی: {col.label}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="جهت مرتب‌سازی"
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value as SortDirection)}
              >
                <option value="asc">صعودی</option>
                <option value="desc">نزولی</option>
              </Select>
            </div>
          }
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowTone={(r) => (r.status === 'inactive' ? 'neutral' : undefined)}
          legend={hasStatusField ? [{ tone: 'neutral', label: 'غیرفعال' }] : undefined}
          zebra
          emptyState={
            <V2EmptyState
              icon={config.icon}
              title={`${config.label}ی برای نمایش وجود ندارد`}
              description={`اولین ${config.label} را ثبت کنید.`}
              action={
                <Link to={`${base}/master-data/${config.pathSegment}/new`}>
                  <V2Button variant="primary" icon="plus">
                    {`ایجاد ${config.label}`}
                  </V2Button>
                </Link>
              }
            />
          }
        />
      ) : null}

      {archiveError ? (
        <Alert tone="danger" title="خطا">
          {archiveError}
        </Alert>
      ) : null}

      {pendingArchive ? (
        <ConfirmDialog
          open
          title={`آرشیو ${config.label}`}
          message={`آیا از آرشیو «${String(pendingArchive.name ?? pendingArchive.code ?? '')}» اطمینان دارید؟ این مورد غیرفعال می‌شود و از فهرست فعال‌ها خارج می‌شود؛ بعداً از طریق فرم ویرایش قابل بازگرداندن است.`}
          confirmLabel="آرشیو"
          cancelLabel="انصراف"
          loading={archiving}
          onConfirm={() => void onConfirmArchive()}
          onCancel={() => setPendingArchive(null)}
        />
      ) : null}
    </div>
  );
}
